import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { PublicFormService, DirectoryResult } from '../../../services/public-form.service';
import { NO_PHOTO, fallbackToNoPhoto } from '../../../shared/utils/no-photo';

@Component({
  selector: 'app-annuaire',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, TranslatePipe, RouterModule],
  templateUrl: './annuaire.html',
  styleUrl: './annuaire.css',
})
export class AnnuaireComponent implements OnInit {
  /** Image par défaut des annuaires, en l'absence de photo. */
  readonly noPhoto = NO_PHOTO;
  readonly onImgError = fallbackToNoPhoto;

  query = '';
  /** L'onglet « licences » a été retiré : l'annuaire liste les
      influenceurs, les agences et les calligraphes. */
  activeTab: 'influencers' | 'agencies' | 'calligraphers' = 'influencers';
  loading = false;
  error = '';

  influencers: any[] = [];
  agencies: any[] = [];
  calligraphers: any[] = [];
  stats: any = null;

  // ── Filtre par secteur ──────────────────────────────────────────────────
  selectedSector = '';

  /** Ne montrer que les profils dont une licence est suspendue. */
  onlySuspended = false;
  influencerSectors: string[] = [];
  agencySectors: string[] = [];
  calligrapherSectors: string[] = [];

  constructor(
    private publicFormService: PublicFormService,
    private router: Router,
    private translate: TranslateService,
  ) {}

  // ── Pagination ──────────────────────────────────────────────────────────
  /** Taille de page côté serveur (plafonnée à 50 par l'API). */
  private readonly API_PER_PAGE = 50;
  /** Garde-fou : nombre max de pages récupérées (50 x 20 = 1000 fiches). */
  private readonly MAX_PAGES = 20;
  /** Nombre de fiches affichées par page dans l'interface. */
  readonly pageSize = 12;
  currentPage = 1;

  ngOnInit(): void {
    this.search();
  }

  /**
   * Charge l'annuaire complet.
   * L'API pagine (12 par défaut, 50 max) : on récupère la 1re page puis, si
   * `meta.last_page` l'indique, les suivantes en parallèle. Le jeu complet est
   * nécessaire car le filtre par secteur et les compteurs d'onglets travaillent
   * côté client sur l'ensemble des fiches.
   */
  search() {
    this.loading = true;
    this.error = '';
    this.currentPage = 1;

    this.publicFormService
      .searchDirectory(this.query, 'all', 1, this.API_PER_PAGE)
      .subscribe({
        next: (res: DirectoryResult) => {
          this.influencers   = res.influencers?.data ?? [];
          this.agencies      = res.agencies?.data ?? [];
          this.calligraphers = (res as any).calligraphers?.data ?? [];
          this.stats       = res.stats ?? null;

          const lastPage = Math.max(
            res.influencers?.meta?.last_page ?? 1,
            res.agencies?.meta?.last_page ?? 1,
            (res as any).calligraphers?.meta?.last_page ?? 1,
          );
          const pages = Math.min(lastPage, this.MAX_PAGES);

          if (pages <= 1) {
            this.finishLoad();
            return;
          }

          // Pages 2..N en parallèle. Les trois collections partagent le même
          // paramètre `page` : une collection déjà épuisée renvoie simplement
          // un tableau vide, sans effet sur la concaténation.
          const rest = [];
          for (let p = 2; p <= pages; p++) {
            rest.push(this.publicFormService.searchDirectory(this.query, 'all', p, this.API_PER_PAGE));
          }

          forkJoin(rest).subscribe({
            next: (others: DirectoryResult[]) => {
              others.forEach(r => {
                this.influencers.push(...(r.influencers?.data ?? []));
                this.agencies.push(...(r.agencies?.data ?? []));
                this.calligraphers.push(...((r as any).calligraphers?.data ?? []));
              });
              this.finishLoad();
            },
            // Les pages suivantes ont échoué : on garde au moins la première.
            error: () => this.finishLoad(),
          });
        },
        error: () => {
          this.loading = false;
          this.error = 'Impossible de charger les données. Veuillez réessayer.';
        },
      });
  }

  private finishLoad(): void {
    this.loading = false;
    this.sortAll();
    this.extractSectors();
  }

  /**
   * Classe les trois listes par ordre alphabétique.
   *
   * L'API trie déjà, mais les pages sont récupérées puis concaténées, et le
   * classement SQL ignore les accents comme l'alphabet arabe. `localeCompare`
   * avec la langue courante donne l'ordre attendu par le lecteur.
   */
  private sortAll(): void {
    this.influencers.sort((a, b) => this.compareNames(a, b));
    this.agencies.sort((a, b) => this.compareNames(a, b));
    this.calligraphers.sort((a, b) => this.compareNames(a, b));
  }

  /** Nom affiché d'une fiche, quel que soit son type. */
  private entryName(e: any): string {
    return (e?.display_name ?? e?.agency_name ?? '').trim();
  }

  private compareNames(a: any, b: any): number {
    return this.entryName(a).localeCompare(
      this.entryName(b),
      this.translate.currentLang || 'fr',
      { sensitivity: 'base', numeric: true },
    );
  }

  setTab(tab: 'influencers' | 'agencies' | 'calligraphers') {
    this.activeTab = tab;
    this.selectedSector = ''; // reset filtre au changement d'onglet
    this.currentPage = 1;
  }

  // ── Filtre secteur ──────────────────────────────────────────────────────

  /** Extrait les secteurs uniques depuis influencers.niche et agencies.services. */
  private extractSectors(): void {
    const infSet = new Set<string>();
    this.influencers.forEach(i => {
      const v = (i.niche ?? '').trim();
      if (v) infSet.add(v);
    });
    this.influencerSectors = [...infSet].sort((a, b) => a.localeCompare(b, 'fr'));

    const agSet = new Set<string>();
    this.agencies.forEach(a => {
      const raw = (a.services ?? '').trim();
      if (!raw) return;
      // Les services peuvent être séparés par , ; ou /
      raw.split(/[,;/]/).forEach((s: string) => {
        const t = s.trim();
        if (t) agSet.add(t);
      });
    });
    this.agencySectors = [...agSet].sort((a, b) => a.localeCompare(b, 'fr'));

    // Les calligraphes portent leur métier dans `specialty`.
    const calSet = new Set<string>();
    this.calligraphers.forEach(c => {
      const v = (c.specialty ?? '').trim();
      if (v) calSet.add(v);
    });
    this.calligrapherSectors = [...calSet].sort((a, b) => a.localeCompare(b, 'fr'));
  }

  /** Liste de secteurs proposée selon l'onglet actif. */
  get availableSectors(): string[] {
    return this.activeTab === 'influencers'   ? this.influencerSectors
         : this.activeTab === 'calligraphers' ? this.calligrapherSectors
         : this.agencySectors;
  }

  /** Calligraphes filtrés par spécialité. */
  /** Calligraphes filtrés par spécialité, puis par suspension. */
  get filteredCalligraphers(): any[] {
    return this.applyFilters(this.calligraphers, c => c.specialty);
  }

  /** Influenceurs filtrés par secteur (niche), puis par suspension. */
  get filteredInfluencers(): any[] {
    return this.applyFilters(this.influencers, i => i.niche);
  }

  /** Agences filtrées par secteur (services), puis par suspension. */
  get filteredAgencies(): any[] {
    return this.applyFilters(this.agencies, a => a.services);
  }

  /**
   * Filtres communs aux trois onglets : secteur, puis licence suspendue.
   * Chaque onglet ne diffère que par le champ qui porte son secteur.
   */
  private applyFilters(list: any[], sectorOf: (x: any) => string | null | undefined): any[] {
    let out = list;

    if (this.selectedSector) {
      const s = this.selectedSector.toLowerCase();
      out = out.filter(x => (sectorOf(x) ?? '').toLowerCase().includes(s));
    }

    if (this.onlySuspended) {
      out = out.filter(x => this.isSuspended(x));
    }

    return out;
  }

  /** Vrai si le profil a au moins une licence suspendue. */
  isSuspended(item: any): boolean {
    return (item?.suspended_licenses ?? 0) > 0;
  }

  /**
   * Profils suspendus de l'onglet actif, secteur compris : c'est exactement
   * ce que le filtre affichera une fois activé.
   */
  get suspendedInTab(): number {
    const [base, sectorOf] =
      this.activeTab === 'influencers'   ? [this.influencers,   (x: any) => x.niche]
    : this.activeTab === 'calligraphers' ? [this.calligraphers, (x: any) => x.specialty]
    :                                      [this.agencies,      (x: any) => x.services];

    const s = this.selectedSector.toLowerCase();
    return base.filter(x =>
      this.isSuspended(x) && (!s || (sectorOf(x) ?? '').toLowerCase().includes(s))
    ).length;
  }

  toggleSuspended(): void {
    this.onlySuspended = !this.onlySuspended;
    this.currentPage = 1;
  }

  selectSector(sector: string): void {
    this.selectedSector = this.selectedSector === sector ? '' : sector;
    this.currentPage = 1;
  }

  clearSector(): void {
    this.selectedSector = '';
    this.currentPage = 1;
  }

  // ── Pagination côté client ──────────────────────────────────────────────

  /** Nombre total de fiches de l'onglet actif (après filtre secteur). */
  get totalItems(): number {
    return this.activeTab === 'influencers'   ? this.filteredInfluencers.length
         : this.activeTab === 'calligraphers' ? this.filteredCalligraphers.length
         : this.filteredAgencies.length;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
  }

  private slice<T>(items: T[]): T[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return items.slice(start, start + this.pageSize);
  }

  get pagedInfluencers(): any[] { return this.slice(this.filteredInfluencers); }
  get pagedAgencies(): any[]    { return this.slice(this.filteredAgencies); }
  get pagedCalligraphers(): any[] { return this.slice(this.filteredCalligraphers); }

  /** Indices affichés, ex. « 13–24 sur 47 ». */
  get rangeStart(): number {
    return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }
  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  /** Numéros de page à afficher, avec ellipses si la liste est longue. */
  get pageNumbers(): (number | '…')[] {
    const total = this.totalPages;
    const cur   = this.currentPage;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages: (number | '…')[] = [1];
    const from = Math.max(2, cur - 1);
    const to   = Math.min(total - 1, cur + 1);
    if (from > 2) pages.push('…');
    for (let p = from; p <= to; p++) pages.push(p);
    if (to < total - 1) pages.push('…');
    pages.push(total);
    return pages;
  }

  goToPage(page: number | '…'): void {
    if (page === '…') return;
    const target = Math.min(Math.max(1, page), this.totalPages);
    if (target === this.currentPage) return;
    this.currentPage = target;
    // Remonte en haut de la liste pour éviter de rester en bas de page.
    if (typeof window !== 'undefined') {
      document.querySelector('.arp-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  prevPage(): void { this.goToPage(this.currentPage - 1); }
  nextPage(): void { this.goToPage(this.currentPage + 1); }

  // ── Navigation cartes ───────────────────────────────────────────────────

  openAgency(agency: any) {
    if (!agency.is_public) return;
    this.router.navigate(['/annuaire/agence', agency.id]);
  }

  openInfluencer(influencer: any) {
    if (!influencer.is_public) return;
    this.router.navigate(['/annuaire/influenceur', influencer.id]);
  }

  openCalligrapher(calligrapher: any) {
    if (!calligrapher.is_public) return;
    this.router.navigate(['/annuaire/calligraphe', calligrapher.id]);
  }

  // ── Helpers d'affichage ─────────────────────────────────────────────────


  formatFollowers(n: number): string {
    if (!n) return '—';
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(0) + 'K';
    return String(n);
  }

  platformIcon(p: string): string {
    const m: Record<string, string> = {
      instagram: '📷', tiktok: '🎵', youtube: '▶️',
      twitter: '𝕏', facebook: '📘', snapchat: '👻',
      linkedin: '💼', autre: '🌐',
    };
    return m[p] || '🌐';
  }
}
